// ---------------------------------------------------------------------------
// UploadZone — the whole path from "I have files" to "they're in the grid":
//   - drag-drop anywhere on the Media panel (window-level listeners, since the
//     panel IS the screen while it's open) + the header Upload button (routed
//     through mediaStore.pickerRequest, same timestamp-signal idiom as
//     uiStore.composerRequest);
//   - a confirm strip where the uploader picks the AUDIENCE before anything
//     moves: one project's members — or "Everyone at CG", which is owner-only;
//   - per-file progress rows; failures stay visible with the reason.
// The store owns the actual byte-moving (rawUpload) and the optimistic insert.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCloud, X } from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import { Button, fieldClassSm } from '@/core/ui/atoms';
import { useToastStore } from '@/core/ui/toastStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { humanSize, useMediaStore, MAX_UPLOAD_BYTES } from './mediaStore';

// "Everyone at CG" is a broadcast to all ~50 people, and that reach is OWNER-ONLY
// (Pat/Allyson, 2026-07-27: "They should never be able to upload company wide.
// Someone could put something crazy up."). The server enforces it (403); this file
// just doesn't offer the option — and for non-owners there is NO default audience:
// -1 = "you haven't said who this is for yet", and Upload stays disabled until
// they pick a project. A default that broadcasts by momentum is the failure mode.
const NO_AUDIENCE = -1;

export function UploadZone() {
  const uploads = useMediaStore((s) => s.uploads);
  const uploadFiles = useMediaStore((s) => s.uploadFiles);
  const dismissUpload = useMediaStore((s) => s.dismissUpload);
  const pickerRequest = useMediaStore((s) => s.pickerRequest);
  const filterAreaId = useMediaStore((s) => s.filter.areaId);
  const areas = useAreaStore((s) => s.areas);
  const isAdmin = Boolean(useAuthStore((s) => s.user?.isAdmin));
  const toast = useToastStore((s) => s.show);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [staged, setStaged] = useState<File[]>([]);
  // 0 = "Everyone at CG" (owner-only), a project id, or NO_AUDIENCE (unchosen).
  const [audience, setAudience] = useState<number>(NO_AUDIENCE);
  const [dragOver, setDragOver] = useState(false);

  // Header Upload button → file picker. Consume only NEW requests: without the
  // ref compare, remounting the panel would replay the last press into a
  // surprise file dialog.
  const consumedRequest = useRef(pickerRequest);
  useEffect(() => {
    if (pickerRequest !== consumedRequest.current) {
      consumedRequest.current = pickerRequest;
      inputRef.current?.click();
    }
  }, [pickerRequest]);

  const stagedEmpty = staged.length === 0;
  const stage = useCallback((list: FileList | File[]) => {
    const ok: File[] = [];
    for (const f of Array.from(list)) {
      // The size gate lives HERE, before the confirm strip — finding out after
      // you've picked an audience and pressed Upload is the annoying version.
      if (f.size > MAX_UPLOAD_BYTES) { toast(`${f.name} is over 150 MB — skipped.`, 'error'); continue; }
      ok.push(f);
    }
    if (!ok.length) return;
    // First files in an empty strip inherit the grid's project filter as the
    // default audience — you're looking at Apopka's media, you're probably
    // adding TO Apopka. Otherwise: owners default to Everyone-at-CG; everyone
    // else gets NO default and must say who it's for.
    if (stagedEmpty) setAudience(filterAreaId ?? (isAdmin ? 0 : NO_AUDIENCE));
    setStaged((prev) => [...prev, ...ok]);
  }, [toast, filterAreaId, stagedEmpty, isAdmin]);

  // Drag-drop over the whole panel. Depth-counted: dragenter/leave fire for
  // every child crossed, and a naive boolean flickers the overlay off mid-drag.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => { if (!hasFiles(e)) return; depth++; setDragOver(true); };
    const onLeave = (e: DragEvent) => { if (!hasFiles(e)) return; depth = Math.max(0, depth - 1); if (depth === 0) setDragOver(false); };
    const onOver = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragOver(false);
      if (e.dataTransfer?.files.length) stage(e.dataTransfer.files);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [stage]);

  const startUpload = () => {
    if (audience === NO_AUDIENCE) return; // button is disabled; belt and braces
    const files = staged;
    setStaged([]);
    void uploadFiles(files, audience === 0 ? null : audience);
  };

  const stripVisible = staged.length > 0 || uploads.length > 0;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.currentTarget.files?.length) stage(e.currentTarget.files);
          e.currentTarget.value = ''; // same file twice must fire change twice
        }}
      />

      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center border-2 border-dashed border-brand bg-brand/10">
          <div className="flex items-center gap-2 rounded-lg bg-canvas px-4 py-3 text-sm font-medium text-ink shadow-popover">
            <Icon i={UploadCloud} size={16} className="text-brand" />
            Drop files to add them to the library
          </div>
        </div>
      )}

      {stripVisible && (
        <div className="absolute bottom-4 left-1/2 z-40 w-[min(560px,calc(100%-2rem))] -translate-x-1/2 rounded-lg border border-line bg-canvas p-3 shadow-popover">
          {staged.length > 0 && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-semibold text-ink">
                  Upload {staged.length} file{staged.length === 1 ? '' : 's'}
                </span>
                <button
                  onClick={() => setStaged([])}
                  className="ml-auto rounded p-1 text-muted transition hover:bg-hover hover:text-ink"
                  title="Cancel"
                >
                  <Icon i={X} size={14} />
                </button>
              </div>
              <div className="mb-2 max-h-36 space-y-1 overflow-y-auto">
                {staged.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md bg-page/60 px-2 py-1">
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">{f.name}</span>
                    <span className="shrink-0 text-2xs tabular-nums text-faint">{humanSize(f.size)}</span>
                    <button
                      onClick={() => setStaged((prev) => prev.filter((_, j) => j !== i))}
                      className="shrink-0 text-faint transition hover:text-danger"
                      title="Remove"
                    >
                      <Icon i={X} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-2xs font-semibold uppercase tracking-wide text-faint">Share with</span>
                <div className="min-w-0 flex-1">
                  <select
                    className={fieldClassSm}
                    value={String(audience)}
                    onChange={(e) => setAudience(Number(e.target.value))}
                  >
                    {audience === NO_AUDIENCE && <option value="-1" disabled>Choose who this is for…</option>}
                    {isAdmin && <option value="0">Everyone at CG</option>}
                    {areas.map((a) => (
                      <option key={a.id} value={String(a.id)}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <Button variant="primary" size="sm" onClick={startUpload} disabled={audience === NO_AUDIENCE}>Upload</Button>
              </div>
            </>
          )}

          {uploads.length > 0 && (
            <div className={`space-y-1 ${staged.length > 0 ? 'mt-2 border-t border-line pt-2' : ''}`}>
              {uploads.map((u) => (
                <div key={u.key} className="flex items-center gap-2 rounded-md bg-page/60 px-2 py-1">
                  {u.status === 'uploading' ? (
                    <span aria-hidden className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-line border-t-brand" />
                  ) : (
                    <span className="shrink-0 text-danger"><Icon i={X} size={14} /></span>
                  )}
                  <span className={`min-w-0 flex-1 truncate text-xs ${u.status === 'error' ? 'text-danger' : 'text-ink'}`}>
                    {u.name}
                    {u.status === 'error' && u.message ? ` — ${u.message}` : ''}
                  </span>
                  <span className="shrink-0 text-2xs tabular-nums text-faint">{humanSize(u.size)}</span>
                  {u.status === 'error' && (
                    <button
                      onClick={() => dismissUpload(u.key)}
                      className="shrink-0 text-faint transition hover:text-ink"
                      title="Dismiss"
                    >
                      <Icon i={X} size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
